/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiBasicTableColumn, EuiTableActionsColumnType } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiText } from '@elastic/eui'

import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import { FormattedMessage, Unmask } from '@modules/i18n'
import CloudProvider from '@modules/cloud-components/CloudProvider'
import { getCspRegionIdFromRegionId } from '@modules/cloud-lib/providers'

import TrafficFilterType from '../TrafficFilterType'

import DeleteButton from './DeleteButton'

import type { IntlShape } from 'react-intl'

export const nameColumn: EuiBasicTableColumn<TrafficFilterRulesetInfo> = {
  name: <FormattedMessage id='networkSecurity.list.name.header' defaultMessage='Name' />,
  field: 'name',
  sortable: true,
  render: (_value, ruleset) => (
    <EuiFlexGroup direction='column' gutterSize='none'>
      <EuiFlexItem>{ruleset.name}</EuiFlexItem>
      {ruleset.description && (
        <EuiFlexItem>
          <EuiText size='xs' color='subdued'>
            {ruleset.description}
          </EuiText>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  ),
}

export const typeColumn: EuiBasicTableColumn<TrafficFilterRulesetInfo> = {
  name: (
    <FormattedMessage id='networkSecurity.list.ruleset.type.header' defaultMessage='Filter type' />
  ),
  render: (ruleset: TrafficFilterRulesetInfo) => (
    <div className='eui-textNoWrap'>
      <TrafficFilterType type={ruleset.type} />
    </div>
  ),
}

export const defaultColumn: EuiBasicTableColumn<TrafficFilterRulesetInfo> = {
  name: <FormattedMessage id='networkSecurity.list.default.header' defaultMessage='Default' />,
  render: (ruleset: TrafficFilterRulesetInfo) =>
    ruleset.include_by_default ? (
      <FormattedMessage id='networkSecurity.list.default.yes' defaultMessage='Yes' />
    ) : (
      <FormattedMessage id='networkSecurity.list.default.no' defaultMessage='No' />
    ),
}

export const resourcesColumn: EuiBasicTableColumn<TrafficFilterRulesetInfo> = {
  name: <FormattedMessage id='networkSecurity.list.usage' defaultMessage='Protected resources' />,
  render: (ruleset: TrafficFilterRulesetInfo) => (
    <div className='eui-textNoWrap'>
      {!ruleset.associations ? (
        <FormattedMessage id='networkSecurity.list.ruleset-unused' defaultMessage='Unused' />
      ) : (
        <FormattedMessage
          id='networkSecurity.list.ruleset-association-count'
          defaultMessage='{count} {count, plural, one {deployment} other {deployments}}'
          values={{ count: ruleset.total_associations }}
        />
      )}
    </div>
  ),
}

export const providerColumn: EuiBasicTableColumn<TrafficFilterRulesetInfo> = {
  name: (
    <FormattedMessage id='networkSecurity.list.ruleset.provider.header' defaultMessage='Provider' />
  ),
  render: (ruleset: TrafficFilterRulesetInfo) => (
    <div className='eui-textNoWrap'>
      <CloudProvider region={ruleset.region} text='short' showIcon={true} />
    </div>
  ),
}

export const regionColumn: EuiBasicTableColumn<TrafficFilterRulesetInfo> = {
  name: (
    <FormattedMessage id='networkSecurity.list.ruleset.region.header' defaultMessage='Region' />
  ),
  render: (ruleset: TrafficFilterRulesetInfo) => (
    <div className='eui-textNoWrap'>
      <Unmask>{getCspRegionIdFromRegionId(ruleset.region)}</Unmask>
    </div>
  ),
}

export const makeActionsColumn = ({
  formatMessage,
  onEdit,
}: {
  formatMessage: IntlShape['formatMessage']
  onEdit: (ruleset: TrafficFilterRulesetInfo) => void
}) => {
  const actions: EuiTableActionsColumnType<TrafficFilterRulesetInfo>['actions'] = [
    {
      type: 'icon',
      name: <FormattedMessage id='networkSecurity.list.ruleset.edit.name' defaultMessage='Edit' />,
      description: formatMessage({
        id: 'networkSecurity.list.ruleset.edit.description',
        defaultMessage: 'Edit this traffic filter',
      }),
      icon: 'pencil',
      onClick: (ruleset: TrafficFilterRulesetInfo) => onEdit(ruleset),
    },
    {
      render: (ruleset: TrafficFilterRulesetInfo) => <DeleteButton ruleset={ruleset} />,
    },
  ]

  return {
    name: (
      <FormattedMessage id='networkSecurity.list.ruleset.actions.header' defaultMessage='Actions' />
    ),
    actions,
  }
}
